import FingerprintJS from "@fingerprintjs/fingerprintjs";

export interface DeviceCharacteristics {
  screen: {
    width: number;
    height: number;
    depth: number;
  };
  timezone: string;
  language: string;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  maxTouchPoints?: number;
}

export interface DeviceData {
  fingerprint: string;
  characteristics: DeviceCharacteristics;
  ip?: string;
}

const getPublicIP = async (): Promise<string | undefined> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn("Could not fetch public IP:", error);
    return undefined;
  }
};

const generateDeviceCharacteristics = (): DeviceCharacteristics => {
  // Only include attributes that are stable across browser auto-updates,
  // window moves between monitors, and OS minor updates. Fields like
  // navigator.userAgent, window.devicePixelRatio and navigator.platform
  // change frequently and would invalidate the fingerprint unnecessarily.
  return {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      depth: window.screen.colorDepth,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    hardwareConcurrency: navigator.hardwareConcurrency || undefined,
    deviceMemory: (navigator as Navigator & { deviceMemory?: number })
      .deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints || undefined,
  };
};

export const generateDeviceFingerprint = async (): Promise<DeviceData> => {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const characteristics = generateDeviceCharacteristics();
    const ip = await getPublicIP();
    return {
      fingerprint: result.visitorId,
      characteristics,
      ip,
    };
  } catch (error) {
    console.error("Error generating device fingerprint:", error);
    const characteristics = generateDeviceCharacteristics();
    const fallbackFingerprint = btoa(JSON.stringify(characteristics)).substring(
      0,
      32
    );
    return {
      fingerprint: fallbackFingerprint,
      characteristics,
      ip: undefined,
    };
  }
};
